import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AppNotification } from '../types/notification.types';

const MAX_NOTIFICATIONS = 50;

interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  isPanelOpen: boolean;

  // Actions
  addNotification: (n: Omit<AppNotification, 'read'>) => void;
  markAllRead: () => void;
  markRead: (notificationId: string) => void;
  removeNotification: (notificationId: string) => void;
  clearAll: () => void;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      isPanelOpen: false,

      addNotification: (n) => {
        const incoming: AppNotification = { ...n, read: false };
        set((state) => {
          // Deduplicate by notificationId
          const exists = state.notifications.some(
            (x) => x.notificationId === n.notificationId
          );
          if (exists) return state;

          const updated = [incoming, ...state.notifications].slice(
            0,
            MAX_NOTIFICATIONS
          );
          return {
            notifications: updated,
            unreadCount: state.unreadCount + 1,
          };
        });
      },

      markAllRead: () =>
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        })),

      markRead: (notificationId) =>
        set((state) => {
          const target = state.notifications.find(
            (n) => n.notificationId === notificationId
          );
          if (!target || target.read) return state;
          return {
            notifications: state.notifications.map((n) =>
              n.notificationId === notificationId ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        }),

      removeNotification: (notificationId) =>
        set((state) => {
          const target = state.notifications.find(
            (n) => n.notificationId === notificationId
          );
          return {
            notifications: state.notifications.filter(
              (n) => n.notificationId !== notificationId
            ),
            unreadCount:
              target && !target.read
                ? Math.max(0, state.unreadCount - 1)
                : state.unreadCount,
          };
        }),

      clearAll: () => set({ notifications: [], unreadCount: 0 }),

      openPanel: () => {
        set({ isPanelOpen: true });
        // Mark all read when panel opens
        get().markAllRead();
      },
      closePanel: () => set({ isPanelOpen: false }),
      togglePanel: () => {
        const open = !get().isPanelOpen;
        set({ isPanelOpen: open });
        if (open) get().markAllRead();
      },
    }),
    {
      name: 'agent-notifications',
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
      }),
    }
  )
);
